import { emptyItemCounts, matchRewards } from '@runkr/config/economy'
import type { Economy } from '@runkr/config/economy'
import { ordinal } from '../text'

/**
 * The own racer's numbers the reward panel reads (PHASE3 T3.5): a `ResultRow` online (with
 * Phase 2's `xp` / `coinsEarned`, T2.2), or the practice standings (no earned fields).
 */
export interface RewardSource {
  placement: number
  finished: boolean
  /** Shards picked up this race. */
  coins: number
  /** Earned this race, from the server (T2.2); missing = not known, compute or show 0. */
  xp?: number
  coinsEarned?: number
  /** Practice counts its own (the server counts them online). */
  itemsUsed?: number
  /** Hits landed on other runners (practice counts its own). */
  hits?: number
}

export type RewardKey = 'shards' | 'place' | 'league' | 'xp'

/** One `label · icon · value` line of the panel. */
export interface RewardRow {
  key: RewardKey
  label: string
  /** Text before the value (`23 ×`, `4th`, `DNF`, `Coming soon`). */
  detail: string
  /** Medal for places 1–3 (the panel shows its image instead of the ordinal). */
  medal: 1 | 2 | 3 | null
  /** What the row adds; null = no number (League until T3.14). */
  value: number | null
  unit: 'coins' | 'XP' | null
}

export interface RewardRows {
  rows: RewardRow[]
  /** `result` = the server's numbers, `computed` = `matchRewards` here, `none` = zeros. */
  source: 'result' | 'computed' | 'none'
  /** A line under the rows when nothing of this is saved; null online (Phase 2's lines say it). */
  note: string | null
}

/** Shown after the values when they are not the saved reward (D-059's wording). */
export const NOT_SAVED = '(not saved)'
export const PRACTICE_NOTE = 'Practice races are not saved'

function placeDetail(r: RewardSource): Pick<RewardRow, 'detail' | 'medal'> {
  if (!r.finished) return { detail: 'DNF', medal: null }
  const medal = r.placement >= 1 && r.placement <= 3 ? (r.placement as 1 | 2 | 3) : null
  return { detail: ordinal(r.placement), medal }
}

/**
 * The four fixed rows (the reference's Match Result, our wording): shards → coins, the place's
 * coins, League (placeholder until T3.14), XP. Values come from the result row when it carries
 * Phase 2's earned fields; else from `matchRewards` over `economy.json`; else 0 and
 * "(not saved)". Online the shard part never exceeds what was earned (an unpaid racer gets 0).
 */
export function rewardRows(r: RewardSource, economy: Economy | null, practice = false): RewardRows {
  const perCoin = economy?.coins.perCoin ?? 1
  const shards = Math.max(0, r.coins)
  let source: RewardRows['source']
  let shardCoins: number
  let placeCoins: number
  let xp: number
  if (r.xp !== undefined && r.coinsEarned !== undefined) {
    source = 'result'
    shardCoins = Math.min(shards * perCoin, r.coinsEarned)
    placeCoins = r.coinsEarned - shardCoins
    xp = r.xp
  } else if (economy) {
    source = 'computed'
    const earned = matchRewards(
      {
        placement: r.placement,
        finished: r.finished,
        coins: shards,
        itemsUsed: r.itemsUsed ?? 0,
        // Only the counts above feed the rewards; the rest is for daily tasks.
        itemsByKind: emptyItemCounts(),
        crates: 0,
        respawns: 0,
        hits: r.hits ?? 0,
      },
      economy,
    )
    shardCoins = shards * perCoin
    placeCoins = earned.coins - shardCoins
    xp = earned.xp
  } else {
    source = 'none'
    shardCoins = 0
    placeCoins = 0
    xp = 0
  }
  const rows: RewardRow[] = [
    {
      key: 'shards',
      label: 'Shards',
      detail: `${shards} ×`,
      medal: null,
      value: shardCoins,
      unit: 'coins',
    },
    { key: 'place', label: 'Place', ...placeDetail(r), value: placeCoins, unit: 'coins' },
    { key: 'league', label: 'League', detail: 'Coming soon', medal: null, value: null, unit: null },
    { key: 'xp', label: 'XP', detail: '', medal: null, value: xp, unit: 'XP' },
  ]
  const note = practice ? PRACTICE_NOTE : source === 'none' ? NOT_SAVED : null
  return { rows, source, note }
}

/** Rows count up from 0 over this long. */
export const COUNT_UP_MS = 600

/** A row's shown value `ms` after the panel opened: eased from 0 to `target` (pure). */
export function valueAt(ms: number, target: number, durationMs = COUNT_UP_MS): number {
  if (ms <= 0) return 0
  if (ms >= durationMs) return target
  const t = ms / durationMs
  const eased = 1 - (1 - t) * (1 - t)
  return Math.round(target * eased)
}
